import { useContext, useEffect, useRef, useState } from "react";

import TypingTextV2 from "~/components/TypingTextV2";
import StyledContentPanel from "~/components/StyledContentPanel";
import { animated, useSpring } from "react-spring";
import { Greeting } from "~/types";
import StyledButton from "~/components/StyledButton";
import { AppContext } from '~/contexts/AppContext';
import DisplayMedia from "./DisplayMedia";
import { useTranslation } from "react-i18next";

import * as topicUtils from "~/util/topic";

interface GreetingsProps {
    onComplete: () => void;
    greeting: Greeting;
}

const Greetings: React.FC<GreetingsProps> = (props) => {

    const { topicData, setGlassPanelBackdropFilter } = useContext(AppContext)!;

    const animationsStartedRef = useRef<boolean>(false);

    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (!animationsStartedRef.current) {
            animationsStartedRef.current = true;
            setTimeout(() => {
                setGlassPanelBackdropFilter(40, 1, 1);
                titlePanelApi.start({
                    opacity: 1,
                    onRest: () => {
                        startAnimations();
                    }
                });
            }, 1000);
        }
    }, []);

    const startAnimations = () => {
        setState({ ...state, currentMessageFadeIn: 0 });
    };

    const [state, setState] = useState({
        currentMessageFadeIn: -1,
        currentMessageText: -1
    });

    const [titlePanelStyle, titlePanelApi] = useSpring(() => ({
        opacity: 0,
        config: { duration: 400 },
    }));

    const greetingCount = props.greeting.messages.length;

    const messageFadeIns = Array(greetingCount).fill(null).map(() => {
        return useSpring(() => ({
            opacity: 0,
            config: { duration: 400 },
        }));
    });

    const [videoFadeInStyle, videoFadeInApi] = useSpring(() => ({
        opacity: 0,
        config: { duration: 400 },
    }));

    const [continueButtonFadeInStyle, continueButtonFadeInApi] = useSpring(() => ({
        opacity: 0,
        config: { duration: 400 },
    }));

    const getMediaUrl = () => {
        if (props.greeting.mediaUrl)
            return props.greeting.mediaUrl;
        return null;
    };

    const mediaUrl = getMediaUrl();

    useEffect(() => {
        if (state.currentMessageFadeIn > -1) {
            if (messageFadeIns.length === 0) {
                activateGreetingTimer();
            } else {
                messageFadeIns[state.currentMessageFadeIn][1].start({
                    opacity: 1,
                    onRest: () => {
                        setState({ ...state, currentMessageText: state.currentMessageFadeIn });
                    }
                });
            }
        }
    }, [state.currentMessageFadeIn]);

    const continueButtonRef = useRef<HTMLButtonElement>(null);
    const continueButtonVisibleRef = useRef<boolean>(false);
    const [shouldPlay, setShouldPlay] = useState<boolean>(false);

    const activateGreetingTimer = () => {
        if (mediaUrl) {
            videoFadeInApi.start({
                opacity: 1,
                onRest: () => { setShouldPlay(true); }
            });
        } else {
            setTimeout(() => {
                fadeOut();
            }, props.greeting.duration * 1000);
        }
    };

    const fadeOut = () => {
        titlePanelApi.start({
            opacity: 0,
            onRest: () => {
                //setGlassPanelBackdropFilter(0, 1, 1);
                props.onComplete();
            }
        });
    };

    const handleContinueButtonClicked = () => {
        if (continueButtonVisibleRef.current)
            fadeOut();
    };

    const greetingMessages = topicUtils.getGreetingMessages(topicData, i18n.language);

    return (
        <animated.div style={titlePanelStyle}>
            {greetingMessages.map((message, i) => (
                <animated.div key={i} style={{ padding: '20px 20px 0 20px', ...messageFadeIns[i][0] }}>
                    <StyledContentPanel>
                        <TypingTextV2
                            active={state.currentMessageText === i}
                            delay={10}
                            onComplete={() => {
                                if (state.currentMessageFadeIn + 1 < greetingCount)
                                    setState({ ...state, currentMessageFadeIn: state.currentMessageFadeIn + 1 });
                                else
                                    activateGreetingTimer();
                            }}
                        >
                            <h1 key={i}>{message}</h1>

                        </TypingTextV2>
                    </StyledContentPanel>
                </animated.div>
            ))}
            {!mediaUrl ? null :
                <>
                    <animated.div style={{ padding: '20px', ...videoFadeInStyle }}>
                        <div style={{ borderRadius: '20px', overflow: 'hidden', }}>
                            <DisplayMedia
                                shouldPlay={shouldPlay}
                                mediaUrl={mediaUrl}
                                onAfterOneSecond={() => {
                                    continueButtonFadeInApi.start({
                                        opacity: 1,
                                        onRest: () => {
                                            if (continueButtonRef.current)
                                                continueButtonRef.current.style.cursor = 'pointer';
                                            continueButtonVisibleRef.current = true;
                                        }
                                    });
                                }}
                                onMediaEnded={() => {
                                    setTimeout(() => {
                                        fadeOut();
                                    }, props.greeting.duration * 1000);
                                }}
                            />
                        </div>
                    </animated.div>
                    <animated.div style={{ padding: '20px', ...continueButtonFadeInStyle }}>
                        <StyledButton ref={continueButtonRef} style={{ cursor: 'auto' }} onClick={handleContinueButtonClicked}>
                            {t('button_continue')}
                        </StyledButton>
                    </animated.div>
                </>
            }
        </animated.div>
    );
};

export default Greetings;